/* ==========================================================================
   Bundle Builder V8 — <bundle-builder> Web Component
   Loaded only by sections/bundle-builder-v8.liquid.

   Design notes:
   - All product/size/price data is embedded per-card as JSON by Liquid
     (script[data-bundle-json]); no product fetches at runtime, so displayed
     prices can never drift from what gets added to the cart.
   - Single source of truth: this.items = [{ baseId, variantId }]. Everything
     else (slots, totals, nudges, card states) derives from it in render().
   - Persistence stores { baseId, variantId } only; prices are re-resolved
     from the embedded data on load, so a stale session can't show stale
     prices.

   Contracts kept for the rest of the theme:
   - Tag name `bundle-builder` + enableBundleBuilder()/disableBundleBuilder()
     (cart-drawer.js calls these when the bundle is removed from the cart).
   - Cart lines carry properties[_bundle]=true (discount function + drawer
     grouping).
   - ?routine=handle,handle prefill.
   - product-info-modal snippet handles quick view (same data attributes).
   ========================================================================== */

if (!customElements.get('bundle-builder')) {
  const TIERS = [0, 0, 0.1, 0.15, 0.2, 0.2]; // index = item count
  const MAX_ITEMS = 5;

  const money = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  };

  const track = (event, payload = {}) => {
    if (typeof dataLayer !== 'undefined') {
      dataLayer.push({ event, ...payload });
    }
  };

  customElements.define(
    'bundle-builder',
    class BundleBuilderV8 extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.items = []; // [{ baseId, variantId }]
        this.products = new Map(); // baseId -> card payload
        this.byHandle = new Map(); // handle -> card payload
        this.externalProducts = new Map(); // baseId -> payload for ?routine products not in the grid
        this.filters = {}; // groupId -> Set(values)
        this.bundleInCart = false;

        this.readCardData();
        this.cacheEls();
        this.bindCards();
        this.bindFilters();
        this.bindRail();
        this.bindModals();
        this.bindPresets();

        this.restoreState().then(() => {
          this.render();
          this.checkExistingBundle();
          this.applyRoutineParam();
        });
      }

      /* ------------------------------------------------------------ setup */

      readCardData() {
        this.querySelectorAll('[data-bundle-card]').forEach((card) => {
          const json = card.querySelector('[data-bundle-json]');
          if (!json) return;
          try {
            const data = JSON.parse(json.textContent);
            data.el = card;
            data.sizes.forEach((s) => {
              s.price = Number(s.price) || 0;
              s.compareAt = s.compareAt ? Number(s.compareAt) : null;
            });
            this.products.set(data.baseId, data);
            this.byHandle.set(data.handle, data);
          } catch (err) {
            console.error('[bundle-builder] bad card payload', err);
          }
        });
      }

      cacheEls() {
        this.rail = this.querySelector('[data-bundle-rail]');
        this.slots = Array.from(this.querySelectorAll('[data-slot]'));
        this.nudgeEl = this.querySelector('[data-nudge]');
        this.routinesEl = this.querySelector('[data-routines]');
        this.savingsLine = this.querySelector('[data-savings-line]');
        this.savingsPct = this.querySelector('[data-savings-pct]');
        this.savingsAmount = this.querySelector('[data-savings-amount]');
        this.submitBtn = this.querySelector('[data-bundle-submit]');
        this.submitLabel = this.querySelector('[data-submit-label]');
        this.submitPrices = this.querySelector('[data-submit-prices]');
        this.errorEl = this.querySelector('[data-bundle-error]');
        this.railFoot = this.querySelector('[data-rail-foot]');
        this.railDone = this.querySelector('[data-rail-done]');
        this.limitNote = this.querySelector('[data-limit-note]');
        this.countEls = this.querySelectorAll('[data-bundle-count], [data-bundle-count-mobile]');
        this.mobileTotal = this.querySelector('[data-mobile-total]');
        this.resultCount = this.querySelector('[data-result-count]');
        this.chipsRow = this.querySelector('[data-active-chips]');
        this.noResults = this.querySelector('[data-no-results]');
      }

      /* ------------------------------------------------------------ cards */

      bindCards() {
        this.products.forEach((data) => {
          const card = data.el;

          card.querySelectorAll('[data-size-chip]').forEach((chip) => {
            chip.addEventListener('click', () => {
              this.selectSize(data, chip.dataset.variantId);
            });
          });

          card.querySelector('[data-bundle-toggle]')?.addEventListener('click', () => {
            this.toggleProduct(data.baseId);
          });
        });
      }

      selectedSize(data) {
        const chip = data.el.querySelector('[data-size-chip][aria-pressed="true"]');
        const id = chip?.dataset.variantId || data.defaultVariantId;
        return data.sizes.find((s) => s.variantId === id) || data.sizes[0];
      }

      selectSize(data, variantId) {
        data.el.querySelectorAll('[data-size-chip]').forEach((chip) => {
          chip.setAttribute('aria-pressed', chip.dataset.variantId === variantId ? 'true' : 'false');
        });

        // If this product is already in the bundle, swap the variant in place.
        const entry = this.items.find((i) => i.baseId === data.baseId);
        if (entry && entry.variantId !== variantId) {
          entry.variantId = variantId;
          this.render();
        }
      }

      toggleProduct(baseId) {
        const data = this.products.get(baseId);
        const existing = this.items.find((i) => i.baseId === baseId);

        if (existing) {
          this.items = this.items.filter((i) => i.baseId !== baseId);
          track('hairstory_remove_product_from_bundle_grid_click', { product_id: baseId });
        } else {
          if (this.items.length >= MAX_ITEMS || !data) return;
          const size = this.selectedSize(data);
          this.items.push({ baseId, variantId: size.variantId });
          track('hairstory_add_product_to_bundle', { product_id: baseId });
        }
        this.render();
      }

      /* ------------------------------------------------------- entry data */

      // Resolve an item to displayable data, from the grid or (for ?routine=
      // products outside the collection) from the fetched fallback payload.
      resolveEntry(item) {
        const data = this.products.get(item.baseId);
        if (data) {
          const size =
            data.sizes.find((s) => s.variantId === item.variantId) || data.sizes[0];
          return {
            title: size.productTitle || data.title,
            sizeLabel: size.label,
            price: size.price,
            image: data.image,
            variantId: size.variantId
          };
        }
        return this.externalProducts.get(item.baseId) || null;
      }

      /* ------------------------------------------------------ persistence */

      async restoreState() {
        let saved = null;
        try {
          saved =
            JSON.parse(sessionStorage.getItem('bundleBuilderStateV2') || 'null') ||
            this.migrateV1State();
        } catch (err) {
          saved = null;
        }
        if (!saved || !Array.isArray(saved.items)) return;

        for (const item of saved.items.slice(0, MAX_ITEMS)) {
          if (!item || !item.baseId) continue;
          const data = this.products.get(String(item.baseId));
          if (data) {
            const size =
              data.sizes.find((s) => s.variantId === String(item.variantId)) ||
              data.sizes[0];
            this.items.push({ baseId: data.baseId, variantId: size.variantId });
            // Restore the size chip so the card matches the saved selection.
            data.el.querySelectorAll('[data-size-chip]').forEach((chip) => {
              chip.setAttribute(
                'aria-pressed',
                chip.dataset.variantId === size.variantId ? 'true' : 'false'
              );
            });
          } else if (item.external) {
            this.externalProducts.set(String(item.baseId), item.external);
            this.items.push({ baseId: String(item.baseId), variantId: String(item.variantId) });
          }
        }
      }

      // Old builder saved {products: [{baseProductId, variantId, ...}]}.
      migrateV1State() {
        try {
          const v1 = JSON.parse(sessionStorage.getItem('bundleBuilderState') || 'null');
          if (!v1 || !Array.isArray(v1.products)) return null;
          return {
            items: v1.products.map((p) => ({
              baseId: String(p.baseProductId),
              variantId: String(p.variantId)
            }))
          };
        } catch (err) {
          return null;
        }
      }

      saveState() {
        const items = this.items.map((item) => {
          const external = this.externalProducts.get(item.baseId);
          return external ? { ...item, external } : item;
        });
        sessionStorage.setItem('bundleBuilderStateV2', JSON.stringify({ items }));
      }

      clearState() {
        this.items = [];
        sessionStorage.removeItem('bundleBuilderStateV2');
        sessionStorage.removeItem('bundleBuilderState');
      }

      /* --------------------------------------------------------- ?routine= */

      async applyRoutineParam() {
        const routine = new URLSearchParams(window.location.search).get('routine');
        if (!routine || this.items.length > 0) return;

        for (const handle of routine.split(',').map((h) => h.trim())) {
          if (!handle || this.items.length >= MAX_ITEMS) continue;
          const data = this.byHandle.get(handle);
          if (data) {
            if (!this.items.find((i) => i.baseId === data.baseId)) {
              const size = this.selectedSize(data);
              this.items.push({ baseId: data.baseId, variantId: size.variantId });
            }
          } else {
            await this.addExternalProduct(handle);
          }
        }
        this.render();
        this.openRail();
      }

      async addExternalProduct(handle) {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) return;
          const p = await res.json();
          const variant = p.variants && p.variants[0];
          if (!variant) return;
          const baseId = String(p.id);
          if (this.items.find((i) => i.baseId === baseId)) return;
          this.externalProducts.set(baseId, {
            title: p.title,
            sizeLabel: variant.title,
            price: variant.price,
            image: p.featured_image,
            variantId: String(variant.id)
          });
          this.items.push({ baseId, variantId: String(variant.id) });
        } catch (err) {
          console.error('[bundle-builder] routine product failed', handle, err);
        }
      }

      /* ---------------------------------------------------------- presets */

      bindPresets() {
        this.querySelectorAll('[data-preset]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const handles = (btn.dataset.preset || '')
              .split(',')
              .map((h) => h.trim())
              .filter(Boolean);
            if (!handles.length) return;

            this.items = [];
            handles.slice(0, MAX_ITEMS).forEach((handle) => {
              const data = this.byHandle.get(handle);
              if (data && !this.items.find((i) => i.baseId === data.baseId)) {
                const size = this.selectedSize(data);
                this.items.push({ baseId: data.baseId, variantId: size.variantId });
              }
            });
            track('hairstory_bundle_preset_click', {
              preset: btn.querySelector('.bbv8-preset__title')?.textContent.trim()
            });
            this.render();
            if (this.routinesEl) this.routinesEl.open = false;
            this.openRail();
          });
        });

        // Close the routines panel when clicking elsewhere.
        document.addEventListener('click', (e) => {
          if (this.routinesEl?.open && !this.routinesEl.contains(e.target)) {
            this.routinesEl.open = false;
          }
        });
      }

      /* ---------------------------------------------------------- filters */

      bindFilters() {
        this.filterGroups = Array.from(this.querySelectorAll('[data-filter-group]'));

        this.filterGroups.forEach((group) => {
          group.addEventListener('change', () => this.onFilterChange());
        });

        this.querySelectorAll('[data-clear-filters]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.filterGroups.forEach((group) => {
              group.querySelectorAll('input:checked').forEach((input) => {
                input.checked = false;
              });
            });
            this.onFilterChange();
          });
        });

        // Close open filter panels when clicking elsewhere.
        document.addEventListener('click', (e) => {
          this.filterGroups.forEach((group) => {
            if (group.open && !group.contains(e.target)) group.open = false;
          });
        });
      }

      onFilterChange() {
        this.filters = {};
        this.filterGroups.forEach((group) => {
          const values = Array.from(group.querySelectorAll('input:checked')).map(
            (input) => input.value
          );
          if (values.length) this.filters[group.dataset.filterGroup] = values;

          const badge = group.querySelector('[data-filter-active-count]');
          if (badge) {
            badge.textContent = values.length;
            badge.hidden = values.length === 0;
          }
          group.toggleAttribute('data-has-active', values.length > 0);
        });
        this.applyFilters();
        this.renderChips();
      }

      applyFilters() {
        const groupIds = Object.keys(this.filters);
        let visible = 0;

        this.querySelectorAll('[data-card-wrapper]').forEach((wrapper) => {
          const matches = groupIds.every((groupId) => {
            const attr = wrapper.getAttribute(`data-${groupId}`) || '';
            const values = attr.split(',').map((v) => v.trim().toLowerCase());
            return this.filters[groupId].some((wanted) =>
              values.includes(wanted.toLowerCase())
            );
          });
          wrapper.hidden = !matches;
          if (matches) visible += 1;
        });

        // Hide category groups with nothing visible in them.
        this.querySelectorAll('[data-group]').forEach((section) => {
          const anyVisible = section.querySelector('[data-card-wrapper]:not([hidden])');
          section.hidden = !anyVisible;
          const anchor = this.querySelector(`[href="#${section.id}"]`);
          if (anchor) anchor.style.display = anyVisible ? '' : 'none';
        });

        if (this.resultCount) this.resultCount.textContent = visible;
        if (this.noResults) this.noResults.hidden = visible > 0;
      }

      renderChips() {
        if (!this.chipsRow) return;
        const chips = [];
        this.filterGroups.forEach((group) => {
          group.querySelectorAll('input:checked').forEach((input) => {
            chips.push({ group, value: input.value });
          });
        });

        this.chipsRow.hidden = chips.length === 0;
        const clearAll = this.querySelector('.bbv8-clear-all');
        if (clearAll) clearAll.hidden = chips.length === 0;

        this.chipsRow.textContent = '';
        chips.forEach(({ group, value }) => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'bbv8-chip';
          chip.innerHTML = `${value}
            <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M11 1L1 11M1 1L11 11" stroke="currentColor" stroke-width="2"/>
            </svg>`;
          chip.setAttribute('aria-label', `Remove filter ${value}`);
          chip.addEventListener('click', () => {
            const input = Array.from(group.querySelectorAll('input')).find(
              (i) => i.value === value
            );
            if (input) input.checked = false;
            this.onFilterChange();
          });
          this.chipsRow.appendChild(chip);
        });
      }

      /* ------------------------------------------------------------ modals */

      bindModals() {
        this.modals = Array.from(this.querySelectorAll('[data-product-modal]'));

        this.modals.forEach((modal) => {
          modal.querySelector('[data-modal-close]')?.addEventListener('click', () => {
            this.closeModal(modal);
          });
          const backdrop = modal.querySelector('[data-modal-backdrop]');
          backdrop?.addEventListener('click', (e) => {
            if (e.target === backdrop || e.target.parentElement === backdrop) {
              this.closeModal(modal);
            }
          });
          const addBtn = modal.querySelector('[data-modal-add-bundle]');
          addBtn?.addEventListener('click', () => {
            const baseId = addBtn.dataset.modalAddBundle;
            if (!this.items.find((i) => i.baseId === baseId)) {
              this.toggleProduct(baseId);
              track('hairstory_product_modal_add_to_bundle_click', { product_id: baseId });
            }
            this.closeModal(modal);
          });
        });

        document.addEventListener('keydown', (e) => {
          if (e.key !== 'Escape') return;
          const open = this.modals.find((m) => !m.classList.contains('hidden'));
          if (open) this.closeModal(open);
        });

        this.products.forEach((data) => {
          data.el.querySelectorAll('[data-product-info-trigger]').forEach((trigger) => {
            trigger.addEventListener('click', (e) => {
              e.preventDefault();
              const modal = this.modals.find((m) => m.dataset.productModal === data.baseId);
              if (modal) {
                modal.classList.remove('hidden');
                document.body.style.overflow = 'hidden';
              }
            });
          });
        });
      }

      closeModal(modal) {
        modal.classList.add('hidden');
        document.body.style.overflow = '';
      }

      /* -------------------------------------------------------------- rail */

      bindRail() {
        this.querySelector('[data-rail-open]')?.addEventListener('click', () => this.openRail());
        this.querySelector('[data-rail-close]')?.addEventListener('click', () => {
          this.rail?.classList.remove('is-open');
        });
        this.submitBtn?.addEventListener('click', () => this.addBundleToCart());
      }

      openRail() {
        this.rail?.classList.add('is-open');
      }

      /* ------------------------------------------------------------ render */

      totals() {
        const entries = this.items.map((i) => this.resolveEntry(i)).filter(Boolean);
        const subtotal = entries.reduce((sum, e) => sum + e.price, 0);
        const rate = TIERS[Math.min(this.items.length, MAX_ITEMS)];
        const discount = Math.round(subtotal * rate);
        return { subtotal, rate, discount, total: subtotal - discount };
      }

      nudgeCopy(count, rate) {
        switch (count) {
          case 0:
            return 'Add a product to start saving.';
          case 1:
            return 'Add 1 more to unlock <strong>10% off</strong> everything.';
          case 2:
            return "You're saving 10%. 1 more unlocks <strong>15% off</strong>.";
          case 3:
            return "You're saving 15%. 1 more unlocks <strong>20% off</strong>.";
          case 4:
            return "You're saving 20%. Room for 1 more at 20% off.";
          default:
            return 'Max savings unlocked: <strong>20% off</strong> your whole bundle.';
        }
      }

      render() {
        const count = this.items.length;
        const { subtotal, rate, discount, total } = this.totals();
        const atLimit = count >= MAX_ITEMS;

        // Cards
        this.products.forEach((data) => {
          const entry = this.items.find((i) => i.baseId === data.baseId);
          const label = data.el.querySelector('[data-atc-label]');
          if (entry) {
            data.el.dataset.state = 'selected';
            if (label) label.textContent = 'Remove from bundle';
          } else if (atLimit) {
            data.el.dataset.state = 'limit';
            if (label) label.textContent = 'Bundle full. Swap an item';
          } else {
            data.el.dataset.state = 'idle';
            if (label) label.textContent = 'Add to bundle';
          }
          const toggleBtn = data.el.querySelector('[data-bundle-toggle]');
          if (toggleBtn) toggleBtn.disabled = !entry && atLimit;
        });

        // Slots
        this.slots.forEach((slot, index) => {
          const item = this.items[index];
          const itemEl = slot.querySelector('.bbv8-slot__item');
          if (!item) {
            slot.dataset.filled = 'false';
            itemEl.hidden = true;
            return;
          }
          const entry = this.resolveEntry(item);
          if (!entry) return;
          slot.dataset.filled = 'true';
          itemEl.hidden = false;

          const img = slot.querySelector('.bbv8-slot__img');
          img.src = entry.image || '';
          img.alt = entry.title;
          slot.querySelector('[data-slot-title]').textContent = entry.title;
          slot.querySelector('[data-slot-size]').textContent = entry.sizeLabel || '';
          slot.querySelector('[data-slot-prices]').innerHTML =
            rate > 0
              ? `<s>${money(entry.price)}</s>${money(Math.round(entry.price * (1 - rate)))}`
              : money(entry.price);

          const removeBtn = slot.querySelector('[data-slot-remove]');
          removeBtn.onclick = () => {
            this.items = this.items.filter((i) => i !== item);
            track('hairstory_remove_product_from_bundle_progress_click', {
              product_id: item.baseId
            });
            this.render();
          };
        });

        // Nudge + savings
        if (this.nudgeEl) this.nudgeEl.innerHTML = this.nudgeCopy(count, rate);
        if (this.savingsLine) {
          this.savingsLine.hidden = discount <= 0;
          if (discount > 0) {
            this.savingsPct.textContent = `${Math.round(rate * 100)}% OFF`;
            this.savingsAmount.textContent = `You're saving ${money(discount)}`;
          }
        }
        if (this.limitNote) this.limitNote.hidden = !atLimit;

        // Submit button
        if (this.submitBtn && !this.bundleInCart) {
          this.submitBtn.disabled = count === 0;
          if (count === 0) {
            this.submitLabel.textContent = 'Add a product to start';
            this.submitPrices.innerHTML = '';
          } else {
            this.submitLabel.textContent = 'Add to cart -';
            this.submitPrices.innerHTML =
              discount > 0
                ? `<s>${money(subtotal)}</s>${money(total)}`
                : money(total);
          }
        }

        // Counts + mobile bar
        this.countEls.forEach((el) => (el.textContent = `(${count})`));
        if (this.mobileTotal) {
          this.mobileTotal.textContent = count > 0 ? `· ${money(total)}` : '';
        }

        this.saveState();
      }

      /* ------------------------------------------------------- add to cart */

      async addBundleToCart() {
        if (!this.items.length || this.bundleInCart) return;

        const entries = this.items
          .map((i) => this.resolveEntry(i))
          .filter(Boolean);

        this.submitBtn.classList.add('is-loading');
        this.errorEl.hidden = true;

        try {
          const formData = new FormData();
          entries.forEach((entry, index) => {
            formData.append(`items[${index}][id]`, entry.variantId);
            formData.append(`items[${index}][quantity]`, 1);
            formData.append(`items[${index}][properties][_bundle]`, 'true');
          });

          let data;
          if (window.CartAPI) {
            data = await window.CartAPI.add(formData);
          } else {
            formData.append('sections', 'v2-cart-drawer,v2-cart-icon-bubble');
            const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
              method: 'POST',
              body: formData
            });
            if (!res.ok) throw new Error(`Add failed: ${res.status}`);
            data = await res.json();
          }

          track('hairstory_add_bundle_to_cart');

          // Bundle is in the cart now — clear the builder and show the
          // done panel (one bundle per order).
          this.clearState();
          this.setBundleInCart(true);
          this.render();

          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderSections === 'function') {
            drawer.renderSections(data.sections, 'add', 'Custom Bundle');
            drawer.open();
          }
          this.rail?.classList.remove('is-open');

          document.dispatchEvent(
            new CustomEvent('cart-update', {
              detail: { source: 'bundle-builder', cartData: data },
              bubbles: true
            })
          );
        } catch (err) {
          console.error('[bundle-builder] add to cart failed', err);
          this.errorEl.textContent =
            (err && err.message) || 'Something went wrong. Please try again.';
          this.errorEl.hidden = false;
        } finally {
          this.submitBtn.classList.remove('is-loading');
        }
      }

      async checkExistingBundle() {
        try {
          const res = await fetch(`${window.Shopify.routes.root}cart.js`);
          const cart = await res.json();
          const hasBundle = cart.items.some((item) => item.properties?._bundle);
          if (hasBundle) this.setBundleInCart(true);
        } catch (err) {
          // Non-fatal: builder stays enabled.
        }
      }

      setBundleInCart(inCart) {
        this.bundleInCart = inCart;
        if (this.railFoot) this.railFoot.hidden = inCart;
        if (this.railDone) this.railDone.hidden = !inCart;
      }

      /* ------------------------------- public API used by cart-drawer.js */

      enableBundleBuilder() {
        this.setBundleInCart(false);
        this.render();
      }

      disableBundleBuilder() {
        this.setBundleInCart(true);
      }
    }
  );
}
